import { useCallback, useMemo } from 'react'

import { GroupChatCreationPermission } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useNetwork, useUpdateNetwork } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

export const GroupMessagingSettings = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { mutateAsync: updateNetwork, isLoading } = useUpdateNetwork()

  const { privateMessagingEnabled, whoCanCreateGroups } =
    network?.settings?.privateMessaging || {}

  const onSubmit = useCallback(
    async (data: { whoCanCreateGroups: GroupChatCreationPermission }) => {
      try {
        await updateNetwork({
          input: {
            settings: {
              privateMessaging: {
                ...network?.settings?.privateMessaging,
                ...data,
              },
            },
          },
        })
        toast({
          title: $t({
            defaultMessage: 'Messaging settings updated.',
            id: 'Admin.Messaging.Settings.Updated',
          }),
          status: 'success',
        })
      } catch (error) {
        toast({
          title: $t({
            defaultMessage: 'Unable to update messaging settings.',
            id: 'Admin.Messaging.Settings.FailedToUpdate',
          }),
          status: 'error',
        })
      }
    },
    [network?.settings?.privateMessaging, $t, updateNetwork],
  )

  const defaultValues = useMemo(() => {
    return {
      whoCanCreateGroups:
        whoCanCreateGroups ?? GroupChatCreationPermission.Staff,
    }
  }, [whoCanCreateGroups])

  return (
    <Card>
      <Card.Header
        title={$t({
          defaultMessage: 'Creating group chats',
          id: 'Admin.Messaging.GroupChats',
        })}
        description={$t({
          defaultMessage:
            'Define who can start group chats in your {COMMUNITY}.',
          id: 'Admin.Messaging.GroupChats.Description.Community',
        })}
      />
      <Card.Content>
        <Form defaultValues={defaultValues} onSubmit={onSubmit}>
          {({ formState }) => (
            <Form.Layout>
              <Form.RadioGroup
                name="whoCanCreateGroups"
                disabled={privateMessagingEnabled === false}
              >
                <RadioGroup.Items>
                  <RadioGroup.Item
                    value={GroupChatCreationPermission.Anyone}
                    title={$t({
                      defaultMessage: '{MEMBERS_CC} can create group chats',
                      id: 'Admin.Messaging.GroupChats.All.Capital',
                    })}
                    description={$t({
                      defaultMessage: 'Any {MEMBER} can start a group chat.',
                      id: 'Admin.Messaging.GroupChats.All.Description.Single',
                    })}
                  />
                  <RadioGroup.Item
                    value={GroupChatCreationPermission.Staff}
                    title={$t({
                      defaultMessage: "{MEMBERS_CC} can't create group chats",
                      id: 'Admin.Messaging.GroupChats.Limited.Member.Capital',
                    })}
                    description={$t({
                      defaultMessage:
                        'Group chat creation is restricted to admins and moderators.',
                      id: 'Admin.Messaging.GroupChats.Limited.Description',
                    })}
                  />
                </RadioGroup.Items>
              </Form.RadioGroup>
              <Form.Actions>
                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={
                    privateMessagingEnabled === false || !formState.isDirty
                  }
                >
                  <T defaultMessage="Update" id="Generics.Update" />
                </Button>
              </Form.Actions>
            </Form.Layout>
          )}
        </Form>
      </Card.Content>
    </Card>
  )
}
